import React, { useEffect, useState } from 'react';
import { LevelUpResult } from '../types';

interface LevelUpModalProps {
  levelUp: LevelUpResult | null;
  onClose: () => void;
}

const LevelUpModal: React.FC<LevelUpModalProps> = ({ levelUp, onClose }) => {
  const [showFlash, setShowFlash] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (levelUp) {
      // Flash effect
      setTimeout(() => setShowFlash(false), 300);
      setTimeout(() => setShowContent(true), 400);
      
      // Auto-close after 5 seconds
      const timer = setTimeout(onClose, 5000);
      return () => clearTimeout(timer);
    }
  }, [levelUp, onClose]);

  if (!levelUp) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[9999]" style={{ zIndex: 9999 }}>
      {/* White Flash Effect */}
      {showFlash && (
        <div className="absolute inset-0 bg-white animate-fade-out" />
      )}
      
      {/* Dark Background with Blur */}
      <div className="absolute inset-0 bg-black/95 backdrop-blur-md" />
      
      {/* Radial glow effect */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
        <div className="w-[800px] h-[800px] bg-yellow-500/30 rounded-full blur-[150px] animate-pulse"></div>
        <div className="absolute w-[600px] h-[600px] bg-blue-500/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '0.5s' }}></div>
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-yellow-400 rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 3}s`,
              opacity: Math.random() * 0.7 + 0.3,
            }}
          />
        ))}
      </div>

      <div className="relative animate-level-up max-w-md w-full mx-3 sm:mx-4">
        {/* Main Modal */}
        <div className="system-window rounded-lg corner-decoration overflow-hidden">
          {/* Animated Background */}
          <div className="absolute inset-0 opacity-10">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                className="absolute h-px bg-gradient-to-r from-transparent via-system-blue to-transparent"
                style={{
                  top: `${i * 12.5}%`,
                  left: 0,
                  right: 0,
                  animation: `pulse ${2 + i * 0.3}s ease-in-out infinite`,
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            ))}
          </div>

          <div className="relative p-4 sm:p-6 text-center">
            {/* Header */}
            {showContent && (
              <div className="mb-3 sm:mb-4 animate-slide-in">
                <div className="flex justify-center mb-2 sm:mb-3 animate-float">
                  <img 
                    src="/icons/icon-levelup.png" 
                    alt="Level Up"
                    className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                    style={{ 
                      filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 1)) drop-shadow(0 0 40px rgba(255, 215, 0, 0.6))'
                    }}
                  />
                </div>
                <div className="text-[10px] sm:text-xs text-system-blue font-orbitron tracking-widest uppercase mb-1 sm:mb-2">
                  ［ System Notification ］
                </div>
                <h2 className="text-4xl sm:text-7xl font-black font-orbitron mb-2 sm:mb-3 tracking-wider animate-glow-pulse"
                    style={{
                      textShadow: `
                        0 0 10px rgba(255,215,0,0.8),
                        0 0 20px rgba(255,215,0,0.6),
                        0 0 30px rgba(255,215,0,0.4),
                        0 0 40px rgba(0,168,255,0.3),
                        0 0 70px rgba(0,168,255,0.2)
                      `,
                      color: '#FFD700'
                    }}>
                  LEVEL UP!
                </h2>
              </div>
            )}

            {/* Level Display */}
            <div className="relative mb-4 sm:mb-6">
              <div className="inline-block relative">
                {/* Glow effect */}
                <div className="absolute inset-0 bg-system-blue/30 blur-2xl rounded-full animate-pulse"></div>
                
                {/* Level Number */}
                <div className="relative bg-gradient-to-br from-system-dark to-system-gray border-3 border-system-blue rounded-lg px-6 sm:px-10 py-3 sm:py-4">
                  <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase tracking-wide mb-0.5 sm:mb-1">
                    New Level
                  </div>
                  <div className="text-4xl sm:text-6xl font-black font-orbitron glow-text">
                    {levelUp.newLevel}
                  </div>
                </div>
              </div>
            </div>

            {/* New Title */}
            {levelUp.newTitle && (
              <div className="mb-4 sm:mb-6 relative">
                <div className="inline-block bg-gradient-to-r from-system-purple/20 to-system-blue/20 border-2 border-system-purple rounded-lg px-4 sm:px-6 py-2 sm:py-3">
                  <div className="text-[10px] sm:text-xs text-gray-400 font-rajdhani uppercase tracking-wider mb-0.5 sm:mb-1">
                    🏆 Title Acquired
                  </div>
                  <div className="text-lg sm:text-2xl font-bold font-orbitron glow-text-purple">
                    {levelUp.newTitle}
                  </div>
                </div>
              </div>
            )}

            {/* Stat Increases */}
            <div className="mb-4 sm:mb-6">
              <div className="bg-system-darker border-2 border-system-blue/30 rounded-lg p-3 sm:p-6">
                <div className="text-xs sm:text-sm text-system-blue font-orbitron uppercase tracking-wider mb-3 sm:mb-4">
                  ⚡ Stat Increases
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-3">
                  {Object.entries(levelUp.statIncreases).map(([stat, value]) => (
                    <div
                      key={stat}
                      className="bg-system-dark border border-green-500/30 rounded p-2 sm:p-3 flex items-center justify-between"
                    >
                      <span className="text-xs sm:text-sm font-rajdhani text-gray-300 uppercase">
                        {stat}
                      </span>
                      <span className="text-base sm:text-xl font-bold font-orbitron text-green-400">
                        +{value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Continue Button */}
            <button
              onClick={onClose}
              className="relative group px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-system-blue to-system-purple rounded-lg font-orbitron font-bold text-sm sm:text-base tracking-wider transition-all hover:scale-105 hover:shadow-neon-blue overflow-hidden"
            >
              <span className="relative z-10">CONTINUE</span>
              <div className="absolute inset-0 bg-gradient-to-r from-system-purple to-system-blue opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>

            {/* Auto-close indicator */}
            <div className="mt-3 sm:mt-4 text-[10px] sm:text-xs text-gray-500 font-rajdhani">
              Auto-closing in 4 seconds...
            </div>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute -top-2 -left-2 sm:-top-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-l-2 sm:border-t-4 sm:border-l-4 border-system-blue animate-pulse"></div>
        <div className="absolute -top-2 -right-2 sm:-top-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-t-2 border-r-2 sm:border-t-4 sm:border-r-4 border-system-blue animate-pulse"></div>
        <div className="absolute -bottom-2 -left-2 sm:-bottom-4 sm:-left-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-l-2 sm:border-b-4 sm:border-l-4 border-system-blue animate-pulse"></div>
        <div className="absolute -bottom-2 -right-2 sm:-bottom-4 sm:-right-4 w-6 h-6 sm:w-8 sm:h-8 border-b-2 border-r-2 sm:border-b-4 sm:border-r-4 border-system-blue animate-pulse"></div>
      </div>
    </div>
  );
};

export default LevelUpModal;
